/**
 * <sticky-filters-header>
 * Web Component that makes the filters header sticky on scroll.
 * Works with both search and collection pages.
 */

class StickyFiltersHeader extends HTMLElement {
  constructor() {
    super()
    this.header = null
    this.siteHeader = null
    this.siteHeaderContainer = null
    this.placeholder = null
    this.lastScroll = 0
    this.scrollDirection = 'down'
    this.headerOffset = 0
    this.headerHeight = 0
    this.siteHeaderHeight = 0
    this.isSticky = false
    this.hasRecalculated = false
    this._boundHandleScroll = this.handleScroll.bind(this)
    this._boundHandleResize = this.calculateOffset.bind(this)
    this._ticking = false

    // Store original title state
    this.titleElement = null
    this.sourceElement = null
    this.originalText = ''
    this.originalClasses = ''
  }

  connectedCallback() {
    // Prevent double initialization
    if (this.hasAttribute('data-initialized')) return
    this.setAttribute('data-initialized', 'true')

    // Try to find either search or collection header
    this.header =
      document.querySelector('.search-filters__header') ||
      document.querySelector('.collection-filters__header')
    if (!this.header) return

    // Find the actual visible header, not the container
    this.siteHeader = document.querySelector('header[role="banner"]')
    // Also keep reference to container for checking scroll state
    this.siteHeaderContainer = document.querySelector('.container-header')

    // Find title element and source element
    const isSearchPage = this.header.classList.contains(
      'search-filters__header'
    )

    // Only apply title replacement for collection pages
    if (!isSearchPage) {
      this.titleElement = this.header.querySelector(
        '.collection-filters__title'
      )
      this.sourceElement = document.querySelector('#titre-collection')

      // Store original title state
      if (this.titleElement) {
        this.originalText = this.titleElement.textContent
        this.originalClasses = this.titleElement.className
      }
    }

    // Create placeholder to prevent layout shift
    this.placeholder = document.createElement('div')
    // Use the appropriate placeholder class based on the header found
    const placeholderClass = isSearchPage
      ? 'search-filters__header-placeholder'
      : 'collection-filters__header-placeholder'
    this.placeholder.className = placeholderClass
    this.placeholder.style.display = 'none'

    // Insert placeholder after header
    this.header.parentNode.insertBefore(
      this.placeholder,
      this.header.nextSibling
    )

    // Initial calculations
    this.calculateOffset()

    // Event listeners
    window.addEventListener('scroll', this._boundHandleScroll, {
      passive: true,
    })
    window.addEventListener('resize', this._boundHandleResize)
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._boundHandleScroll)
    window.removeEventListener('resize', this._boundHandleResize)
    this.removeAttribute('data-initialized')
  }

  calculateOffset() {
    if (!this.header) return

    const rect = this.header.getBoundingClientRect()
    this.headerOffset = rect.top + window.scrollY
    this.headerHeight = this.header.offsetHeight

    if (this.siteHeader) {
      this.siteHeaderHeight = this.siteHeader.offsetHeight
      document.documentElement.style.setProperty(
        '--header-height',
        `${this.siteHeaderHeight}px`
      )
    }
  }

  updateTitle(useSource) {
    if (!this.titleElement || !this.sourceElement) return

    if (useSource) {
      // Get source element's computed styles
      const sourceStyles = window.getComputedStyle(this.sourceElement)

      // Copy text from source element
      this.titleElement.textContent = this.sourceElement.textContent

      // Copy relevant classes from source element
      const sourceClasses = this.sourceElement.className
      this.titleElement.className = sourceClasses

      // Apply inline styles to ensure proper rendering
      this.titleElement.style.fontSize = sourceStyles.fontSize
      this.titleElement.style.fontWeight = sourceStyles.fontWeight
      this.titleElement.style.lineHeight = sourceStyles.lineHeight
      this.titleElement.style.letterSpacing = sourceStyles.letterSpacing
      this.titleElement.style.textTransform = sourceStyles.textTransform
      this.titleElement.style.fontFamily = sourceStyles.fontFamily
    } else {
      // Restore original text and classes
      this.titleElement.textContent = this.originalText
      this.titleElement.className = this.originalClasses

      // Clear inline styles
      this.titleElement.style.fontSize = ''
      this.titleElement.style.fontWeight = ''
      this.titleElement.style.lineHeight = ''
      this.titleElement.style.letterSpacing = ''
      this.titleElement.style.textTransform = ''
      this.titleElement.style.fontFamily = ''
    }
  }

  handleScroll() {
    if (this._ticking) return
    this._ticking = true

    window.requestAnimationFrame(() => {
      // Recalculate offset on first scroll to ensure accurate positioning
      if (!this.hasRecalculated) {
        this.calculateOffset()
        this.hasRecalculated = true
      }

      const currentScroll = window.scrollY
      const isSiteHeaderHidden =
        this.siteHeaderContainer &&
        this.siteHeaderContainer.classList.contains('is-scroll-down')

      this.scrollDirection = currentScroll > this.lastScroll ? 'down' : 'up'

      // Adjust top position when header hides/shows
      if (isSiteHeaderHidden && this.isSticky) {
        /*this.header.style.top = '0px'
        document.documentElement.style.setProperty('--header-height', '0px')*/
      } else if (this.isSticky) {
        // Recalculate header height for Safari viewport changes
        if (this.siteHeader) {
          this.siteHeaderHeight = this.siteHeader.offsetHeight
        }
        /*this.header.style.top = `${this.siteHeaderHeight}px`
        document.documentElement.style.setProperty(
          '--header-height',
          `${this.siteHeaderHeight}px`
          )*/
      }

      const shouldBeSticky =
        currentScroll > this.headerOffset + this.headerHeight
      const shouldRemoveSticky =
        currentScroll <= this.headerOffset - this.siteHeaderHeight
      const isNearDeactivation =
        this.isSticky &&
        currentScroll <= this.headerOffset - this.siteHeaderHeight + 50

      if (shouldBeSticky && !this.isSticky) {
        // Recalculate header height for Safari viewport changes
        if (this.siteHeader) {
          this.siteHeaderHeight = this.siteHeader.offsetHeight
        }

        // Set top position inline for immediate Safari update
        /*this.header.style.top = `${this.siteHeaderHeight}px`*/
        this.header.classList.add('is-sticky')
        this.placeholder.style.display = 'block'
        this.placeholder.style.height = `${this.headerHeight}px`
        this.isSticky = true
        // Also update CSS variable
        /*document.documentElement.style.setProperty(
          '--header-height',
          `${this.siteHeaderHeight}px`
        )*/

        // Update title with source element text and styles
        this.updateTitle(true)
      } else if (shouldRemoveSticky && this.isSticky) {
        this.header.classList.remove('is-sticky')
        this.header.style.top = ''
        this.placeholder.style.display = 'none'
        this.isSticky = false

        // Restore original title
        this.updateTitle(false)
      }

      if (isNearDeactivation) {
        this.header.classList.add('is-fading')
      } else if (this.isSticky) {
        this.header.classList.remove('is-fading')
      }

      this.lastScroll = currentScroll
      this._ticking = false
    })
  }
}

// Define custom element
if (!customElements.get('sticky-filters-header')) {
  customElements.define('sticky-filters-header', StickyFiltersHeader)
}
